import { BankFormConfig, EWalletFormConfig } from "@components/utils/AddFundingProfileForm/types";
import { BANK_INFO_FORM_FIELDS, DEFAULT_CARD_TYPES, EWALLET_INFO_FORM_FIELDS } from "@constants";
import useAuthSession from "@hooks/useAuthSession";
import { useMemberSettings } from "@hooks/useMemberSettings";
import { Currency } from "@types";
import { MemberSettingNameMode, MemberUniqueInfoCollection } from "kz-ui-sdk";
import { useMemo } from "react";

export const MEMBER_UNIQUE_INFO_FORM_FIELD_MAPPING = {
  [MemberUniqueInfoCollection.PAN]: "pan",
  [MemberUniqueInfoCollection.CPF]: "cpf",
  [MemberUniqueInfoCollection.CURP]: "curp",
  [MemberUniqueInfoCollection.DNI]: "pid",
  [MemberUniqueInfoCollection.CE]: "pid",
  [MemberUniqueInfoCollection.PEN_PAS]: "pid",
  [MemberUniqueInfoCollection.RUC]: "pid",
  [MemberUniqueInfoCollection.CC]: "pid",
  [MemberUniqueInfoCollection.NIT]: "pid",
  [MemberUniqueInfoCollection.COP_PAS]: "pid",
} as const;

// Compute available PID type options based on playerInfoCollections
const PID_TYPE_MAP: Record<MemberUniqueInfoCollection, string> = {
  [MemberUniqueInfoCollection.DNI]: "dni",
  [MemberUniqueInfoCollection.CE]: "ce",
  [MemberUniqueInfoCollection.PEN_PAS]: "pen_pas",
  [MemberUniqueInfoCollection.RUC]: "ruc",
  [MemberUniqueInfoCollection.CC]: "cc",
  [MemberUniqueInfoCollection.NIT]: "nit",
  [MemberUniqueInfoCollection.COP_PAS]: "cop_pas",
} as Record<MemberUniqueInfoCollection, string>;

// Helper: Build PID type options based on available collections
const buildPidTypeOptions = (
  playerInfoCollections: MemberUniqueInfoCollection[],
): Array<{ id: string; name: string }> => {
  const allPidTypeOptions = [
    // PEN
    { id: "dni", name: "dni" },
    { id: "ce", name: "ce" },
    { id: "pen_pas", name: "pen_pas" },
    { id: "ruc", name: "ruc" },
    // COP
    { id: "cc", name: "cc" },
    { id: "nit", name: "nit" },
    { id: "cop_pas", name: "cop_pas" },
  ];

  const availablePidTypes = playerInfoCollections
    .filter((collection) => PID_TYPE_MAP[collection])
    .map((collection) => PID_TYPE_MAP[collection]);

  return allPidTypeOptions.filter((option) => availablePidTypes.includes(option.id));
};

// Helper: Split name field into fullName or firstName/lastName
const splitNameFields = (
  config: Record<string, any>,
  nameMode: MemberSettingNameMode | null | undefined,
  legalName: string | null | undefined,
): void => {
  const nameField = config["name"];
  if (!nameField) return;

  const nameEnabled = nameField.enabled && !legalName;

  if (nameMode === MemberSettingNameMode.FullInFirst) {
    config["fullName"] = {
      enabled: !!nameEnabled,
      sortPriority: nameField.sortPriority,
    };
  }
  if (nameMode === MemberSettingNameMode.FirstLast) {
    config["firstName"] = {
      enabled: !!nameEnabled,
      sortPriority: nameField.sortPriority,
    };
    config["lastName"] = {
      enabled: !!nameEnabled,
      sortPriority: (nameField.sortPriority ?? 0) + 1,
    };
  }
  delete config["name"];
};

// Helper: Apply document collection checks and add PID type options
const applyDocumentCollectionChecks = (
  config: Record<string, any>,
  memberInfo: any,
  playerInfoCollections: MemberUniqueInfoCollection[],
): void => {
  const pidCollected = !!(
    memberInfo?.dni ||
    memberInfo?.ce ||
    memberInfo?.pen_pas ||
    memberInfo?.ruc ||
    memberInfo?.cc ||
    memberInfo?.nit ||
    memberInfo?.cop_pas
  );

  const docsCollected = {
    pan: !!memberInfo?.pan,
    cpf: !!memberInfo?.cpf,
    curp: !!memberInfo?.curp,
    pid: pidCollected,
  } as const;

  if (config["pan"]) {
    config["pan"].enabled = config["pan"].enabled && !docsCollected.pan;
  }
  if (config["cpf"]) {
    config["cpf"].enabled = config["cpf"].enabled && !docsCollected.cpf;
  }
  if (config["curp"]) {
    config["curp"].enabled = config["curp"].enabled && !docsCollected.curp;
  }
  if (config["pid"]) {
    config["pid"].enabled = config["pid"].enabled && !docsCollected.pid;
    config["pid"].pidTypeOptions = buildPidTypeOptions(playerInfoCollections);
  }
};

export const useFundingProfileFormConfig = () => {
  const { playerInfoCollections = [], currency, nameMode } = useMemberSettings();
  const { fundingProfileSettings, legalName, self } = useAuthSession();

  const bankFormConfig = useMemo(() => {
    const baseFields = BANK_INFO_FORM_FIELDS[currency as Currency] ?? [];

    // 1) Unique-info fields handling:
    //    Do NOT filter out unsupported unique fields; keep them but set enabled=false
    const uniqueFieldNames = new Set<string>(Object.values(MEMBER_UNIQUE_INFO_FORM_FIELD_MAPPING));
    const mappingByNumber = MEMBER_UNIQUE_INFO_FORM_FIELD_MAPPING;
    const allowedUniqueFieldNames = new Set<string>(
      playerInfoCollections.map((c) => mappingByNumber[c]).filter((v) => !!v),
    );

    // 2) Apply server bank type mapping/options (primarily for INR upi/ifsc)
    const serverBankTypes = fundingProfileSettings?.supportedTypes?.bank?.type ?? [];
    const optionMap: Record<string, string> = { ifsc: "bank", upi: "upi" };
    const mappedBankTypeOptions = serverBankTypes.map((t) => optionMap[t] ?? t);
    const hasServerTypes = mappedBankTypeOptions.length > 0;

    // Build config with ordering
    const intermediateConfig: BankFormConfig = baseFields.reduce(
      (acc: any, field, index) => {
        if (Array.isArray(field)) {
          const [fieldName, options] = field;
          const isBankType = fieldName === "bankTypeOption";
          const isUniqueButNotAllowed = uniqueFieldNames.has(fieldName) && !allowedUniqueFieldNames.has(fieldName);
          acc[fieldName] = {
            enabled: !isUniqueButNotAllowed,
            sortPriority: index,
            options: isBankType && hasServerTypes ? mappedBankTypeOptions : options.options,
          };
        } else {
          const fieldName = field;
          const isUniqueButNotAllowed = uniqueFieldNames.has(fieldName) && !allowedUniqueFieldNames.has(fieldName);
          acc[fieldName] = {
            enabled: !isUniqueButNotAllowed,
            sortPriority: index,
          };
        }
        return acc;
      },
      {} as Record<string, { enabled: boolean; sortPriority: number; options?: string[] }>,
    );

    // If server restricts bank types, toggle subfields accordingly
    if (hasServerTypes) {
      const allowIfsc = mappedBankTypeOptions.includes("bank");
      if (intermediateConfig["ifsc"]) {
        intermediateConfig["ifsc"].enabled = !!allowIfsc;
      }
      if (intermediateConfig["bankTypeOption"]) {
        // Hide selector if only one option remains
        if (mappedBankTypeOptions.length <= 1) {
          intermediateConfig["bankTypeOption"].enabled = false;
        }
        intermediateConfig["bankTypeOption"].options = mappedBankTypeOptions;
      }
    }

    // Split name fields using helper
    splitNameFields(intermediateConfig, nameMode, legalName);

    // Apply document collection checks using helper
    applyDocumentCollectionChecks(intermediateConfig, self?.member?.info || {}, playerInfoCollections);

    // 5) Country-specific helpers
    if ((currency as Currency) === Currency.INR) {
      intermediateConfig.paymentMethodOptions = mappedBankTypeOptions; // ["upi","bank"] typically
      intermediateConfig.labelsByMethod = {
        upi: { accountNumber: "upi id", placeholder: "username@bankname" },
        bank: { accountNumber: "account number", placeholder: "1234 5678 9012 3456" },
      };
      intermediateConfig.ifscToggleAvailable = mappedBankTypeOptions.includes("bank");
    }

    // 6) Card type options from server (PEN, COP)
    if ([Currency.PEN, Currency.COP].includes(currency as Currency) && intermediateConfig["cardType"]) {
      const serverCardTypes = fundingProfileSettings?.supportedTypes?.bank?.cardTypes ?? [];
      intermediateConfig["cardType"].cardTypeOptions =
        serverCardTypes.length > 0 ? serverCardTypes : DEFAULT_CARD_TYPES;
    }

    return intermediateConfig;
  }, [currency, playerInfoCollections, fundingProfileSettings, legalName, nameMode, self]);

  const eWalletFormConfig = useMemo(() => {
    const baseFields = EWALLET_INFO_FORM_FIELDS[currency as Currency] ?? [];

    // 1) Unique-info fields handling:
    //    Do NOT filter out unsupported unique fields; keep them but set enabled=false
    const uniqueFieldNames = new Set<string>(Object.values(MEMBER_UNIQUE_INFO_FORM_FIELD_MAPPING));
    const mappingByNumber = MEMBER_UNIQUE_INFO_FORM_FIELD_MAPPING;
    const allowedUniqueFieldNames = new Set<string>(
      playerInfoCollections.map((c) => mappingByNumber[c]).filter((v) => !!v),
    );

    // Build config with ordering
    const intermediateConfig: Record<string, any> = baseFields.reduce(
      (acc: any, field, index) => {
        if (Array.isArray(field)) {
          const [fieldName, options] = field;
          const isUniqueButNotAllowed = uniqueFieldNames.has(fieldName) && !allowedUniqueFieldNames.has(fieldName);
          acc[fieldName] = {
            enabled: !isUniqueButNotAllowed,
            sortPriority: index,
            options: options.options,
          };
        } else {
          const fieldName = field;
          const isUniqueButNotAllowed = uniqueFieldNames.has(fieldName) && !allowedUniqueFieldNames.has(fieldName);
          acc[fieldName] = {
            enabled: !isUniqueButNotAllowed,
            sortPriority: index,
          };
        }
        return acc;
      },
      {} as Record<string, any>,
    );

    // Split name fields using helper
    splitNameFields(intermediateConfig, nameMode, legalName);

    // Apply document collection checks using helper
    applyDocumentCollectionChecks(intermediateConfig, self?.member?.info || {}, playerInfoCollections);

    return intermediateConfig as EWalletFormConfig;
  }, [currency, playerInfoCollections, legalName, nameMode, self]);

  return {
    bankFormConfig,
    eWalletFormConfig,
  };
};
